/*
  Previous and next buttons for a horizontally scrolling row.

  Scrolls by a viewport rather than by one card, so a click moves a whole
  screenful the way a page turn does. A card at a time means five clicks to
  cross a row of five, which is worse than dragging.

  Hides itself when there is nothing to scroll, and disables each button at
  its end, so the controls never lie about what they will do.
*/
if (!customElements.get('scroll-arrows')) {
  // Slightly less than a full viewport, so the card at the edge stays
  // partly visible and the eye keeps its place across the jump.
  const PAGE_RATIO = 0.85;

  class ScrollArrows extends HTMLElement {
    /*
      A custom element upgrades as soon as its opening tag is parsed, before
      its own children exist, so the buttons are not there yet. Waiting for
      the document defers setup until the markup actually is.
    */
    connectedCallback() {
      if (document.readyState === 'loading') {
        this.onReady = () => this.init();
        document.addEventListener('DOMContentLoaded', this.onReady, { once: true });
        return;
      }

      this.init();
    }

    init() {
      this.prev = this.querySelector('[data-scroll-prev]');
      this.next = this.querySelector('[data-scroll-next]');
      this.scroller = document.getElementById(this.dataset.scroller);
      if (!this.scroller || !this.prev || !this.next) return;

      this.ticking = false;
      this.update = this.update.bind(this);
      this.onScroll = this.onScroll.bind(this);

      this.prev.addEventListener('click', () => this.page(-1));
      this.next.addEventListener('click', () => this.page(1));

      this.scroller.addEventListener('scroll', this.onScroll, { passive: true });

      // Same three triggers as the progress bar: the row resizing, cards
      // arriving later, and images settling after load. Each changes what
      // there is to scroll without the others noticing.
      this.resizeObserver = new ResizeObserver(this.update);
      this.resizeObserver.observe(this.scroller);

      this.mutationObserver = new MutationObserver(this.update);
      this.mutationObserver.observe(this.scroller, { childList: true });

      window.addEventListener('load', this.update, { once: true });

      this.update();
    }

    disconnectedCallback() {
      if (this.onReady) document.removeEventListener('DOMContentLoaded', this.onReady);
      if (this.scroller && this.onScroll) this.scroller.removeEventListener('scroll', this.onScroll);
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.mutationObserver) this.mutationObserver.disconnect();
      if (this.update) window.removeEventListener('load', this.update);
    }

    page(direction) {
      this.scroller.scrollBy({
        left: this.scroller.clientWidth * PAGE_RATIO * direction,
        behavior: 'smooth',
      });
    }

    onScroll() {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        this.update();
        this.ticking = false;
      });
    }

    update() {
      const { scrollWidth, clientWidth, scrollLeft } = this.scroller;
      const maxScroll = scrollWidth - clientWidth;

      // A pixel of slack, because sub pixel layout leaves a fraction over on
      // rows that fit exactly, and arrows that do nothing are worse than no
      // arrows.
      if (maxScroll <= 1) {
        this.setAttribute('hidden', '');
        return;
      }
      this.removeAttribute('hidden');

      this.prev.disabled = scrollLeft <= 1;
      this.next.disabled = scrollLeft >= maxScroll - 1;
    }
  }

  customElements.define('scroll-arrows', ScrollArrows);
}
